
'use client';

import { useState, useRef, ChangeEvent, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFriend } from '@/context/FriendContext';
import { Loader2, Trash2, Edit } from 'lucide-react';
import type { Friend, UpsertFriend } from '@/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useToast } from '@/hooks/use-toast';

interface EditFriendDialogProps {
  friend?: Friend;
  children?: ReactNode;
}

const socialFields: (keyof Friend['socials'])[] = ['instagram', 'behance', 'vimeo', 'artstation', 'twitter'];

export function EditFriendDialog({ friend, children }: EditFriendDialogProps) {
  const { createFriend, updateFriend, deleteFriend } = useFriend();
  const router = useRouter();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [name, setName] = useState(friend?.name || '');
  const [bio, setBio] = useState(friend?.bio || '');
  const [tags, setTags] = useState(friend?.tags?.join(', ') || '');
  const [socials, setSocials] = useState(friend?.socials || {});
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState(friend?.avatarUrl || null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEditing = !!friend;

  const handleAvatarChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };
  
  const handleSocialChange = (field: keyof Friend['socials'], value: string) => {
    setSocials(prev => ({ ...prev, [field]: value }));
  }

  const resetForm = () => {
    setName(friend?.name || '');
    setBio(friend?.bio || '');
    setTags(friend?.tags?.join(', ') || '');
    setSocials(friend?.socials || {});
    setAvatarFile(null);
    setAvatarPreview(friend?.avatarUrl || null);
  };
  
  const onOpenChange = (open: boolean) => {
    if (!open) resetForm();
    setIsOpen(open);
  }

  const handleDelete = async () => {
     if (!friend) return;
     setIsSubmitting(true);
     await deleteFriend(friend.id);
     setIsSubmitting(false);
     setIsOpen(false);
     router.push('/');
  }

  const handleSubmit = async () => {
    if (!name) {
        toast({ title: "Name required", description: "The artist's name is mandatory.", variant: "destructive"});
        return;
    }
    if (!isEditing && !avatarFile) {
        toast({ title: "Avatar required", description: "The profile picture is mandatory.", variant: "destructive"});
        return;
    }
    setIsSubmitting(true);
    const friendData: UpsertFriend = { 
      name, 
      bio, 
      socials, 
      avatarFile: avatarFile || undefined,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean)
    };

    if (isEditing && friend) {
      await updateFriend(friend.id, friendData);
    } else {
      await createFriend(friendData);
    }

    setIsSubmitting(false);
    setIsOpen(false);
  };

  const dialogTrigger = children ? children : (
     <Button variant="secondary">
        <Edit className="mr-2 h-4 w-4" />
        Edit Profile
     </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{dialogTrigger}</DialogTrigger>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Artist' : 'Add Artist'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modify the details of this artist.' : 'Add a new artist to your list.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 py-4">
            <div className="flex items-center gap-4">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={avatarPreview || undefined} />
                  <AvatarFallback>{name?.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                </Avatar>
                <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                  Change Photo
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleAvatarChange}
                />
            </div>
            <div className="space-y-2">
                <Label htmlFor="name">Artist Name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
             <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea id="bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={4}/>
            </div>
             <div className="space-y-2">
                <Label htmlFor="tags">Tags (comma-separated)</Label>
                <Input id="tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Ex: 3D Sculptor, Animator"/>
            </div>

            <div className="space-y-4">
                <Label>Socials (username or handle)</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {socialFields.map(field => (
                        <div key={field} className="space-y-1">
                             <Label htmlFor={`social-${field}`} className="text-xs capitalize text-muted-foreground">{field}</Label>
                             <Input 
                                id={`social-${field}`} 
                                value={socials[field] || ''} 
                                onChange={(e) => handleSocialChange(field, e.target.value)} 
                                placeholder={`@${field}_handle`}
                             />
                        </div>
                    ))}
                </div>
            </div>
        </div>
        <DialogFooter className="justify-between sm:justify-between">
          <div>
            {isEditing && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="destructive" size="icon" disabled={isSubmitting}>
                    <Trash2 className="h-4 w-4"/>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                    <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This action is irreversible and will permanently delete this artist.
                    </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          <div className="flex gap-2">
            <DialogClose asChild><Button type="button" variant="secondary">Cancel</Button></DialogClose>
            <Button type="submit" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save' : 'Create'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
